<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="fr">
        <section class="intro">
          <p>
            La résolution et la taille des fichiers de vos photos 360° pour
            visites virtuelles déterminent la netteté de votre tour sur
            différents appareils, la vitesse de chargement et si les images
            correspondent aux exigences des plateformes comme Google Street
            View, Kuula et Travvir. Utiliser la mauvaise résolution vous coûte
            soit en qualité (trop faible) soit en vitesse et coûts de stockage
            (trop haute). Ce guide couvre les spécifications exactes pour chaque
            plateforme et cas d'usage.
          </p>
        </section>

        <section>
          <h2>Comprendre la résolution des images 360°</h2>

          <h3>Pourquoi les images 360° nécessitent plus de résolution</h3>
          <p>
            Une photo standard s'affiche comme une image plate : sur un écran 4K
            (3 840 × 2 160 px), toute l'image remplit l'écran. Une photo 360°
            s'affiche différemment : la visionneuse n'affiche qu'une partie de
            l'image totale à tout moment — environ 25 % de la largeur totale
            pour un champ de vision de 90°. Cela signifie qu'une photo 360° doit
            avoir <strong>4× la résolution</strong> d'une photo standard
            équivalente pour paraître aussi nette.
          </p>
          <p>
            Une photo 360° qui paraît nette dans une visionneuse de visite
            virtuelle doit faire minimum <strong>8 000 × 4 000 pixels</strong>
            (32 mégapixels).
          </p>

          <h3>Le ratio d'aspect équirectangulaire</h3>
          <p>
            Les photos 360° en projection équirectangulaire ont toujours un
            <strong>ratio 2:1</strong> — deux fois plus larges que hautes.
            Tailles standard : 4 096 × 2 048 (minimum), 8 192 × 4 096
            (recommandé), 11 000 × 5 500 (haute qualité professionnelle).
          </p>
        </section>

        <section>
          <h2>Recommandations par plateforme</h2>
          <table>
            <thead>
              <tr>
                <th>Plateforme</th>
                <th>Résolution recommandée</th>
                <th>Taille de fichier max</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Travvir</td>
                <td>8 000 × 4 000 px</td>
                <td>50 Mo</td>
              </tr>
              <tr>
                <td>Google Street View</td>
                <td>7 168 × 3 584 px</td>
                <td>75 Mo</td>
              </tr>
              <tr>
                <td>Kuula</td>
                <td>8 000 × 4 000 px</td>
                <td>100 Mo</td>
              </tr>
              <tr>
                <td>Facebook 360</td>
                <td>6 000 × 3 000 px</td>
                <td>45 Mo</td>
              </tr>
              <tr>
                <td>Matterport</td>
                <td>Auto (caméra dédiée)</td>
                <td>—</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Résolution produite par les smartphones actuels</h2>
          <p>
            Les smartphones récents produisent des photos 360° au-delà des
            spécifications minimales de la plupart des plateformes :
          </p>
          <ul>
            <li>
              <strong>iPhone 15 Pro</strong> — jusqu'à 12 000 × 6 000 px en
              capture 360° avec Travvir
            </li>
            <li>
              <strong>Samsung Galaxy S24 Ultra</strong> — jusqu'à 10 000 × 5 000
              px
            </li>
            <li><strong>Google Pixel 8</strong> — jusqu'à 8 000 × 4 000 px</li>
          </ul>
        </section>

        <section>
          <h2>Questions fréquentes</h2>

          <h3>Faut-il compresser les images 360° avant publication ?</h3>
          <p>
            Travvir gère automatiquement la compression pour la diffusion web
            tout en conservant le fichier haute résolution pour les exports.
            Vous n'avez pas besoin de compresser manuellement.
          </p>

          <h3>Quelle différence entre JPEG et PNG pour les photos 360° ?</h3>
          <p>
            Le format JPEG est recommandé pour les visites virtuelles car il
            offre un excellent rapport qualité/taille de fichier. Le PNG produit
            des fichiers 3× plus lourds sans amélioration visible dans les
            visionneuses 360°.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Continuez la Lecture</h2>
          <ul>
          <li><a href="/blog/fr/corriger-photos-360-floues">Photos 360° Floues : 6 Causes et Solutions Concrètes 2026</a></li>
          <li><a href="/blog/fr/qu-est-ce-photo-360">Qu'est-ce qu'une Photo 360° ? Guide Complet 2026</a></li>
          <li><a href="/blog/fr/photos-360-avec-smartphone">Comment Prendre des Photos 360° avec un Smartphone : Guide Complet</a></li>
          <li><a href="/blog/fr/integrer-visite-virtuelle-site-web">Comment Intégrer une Visite Virtuelle 360° sur son Site Web 2026</a></li>
          <li><a href="/blog/fr/visite-virtuelle-360-guide-complet">Visite Virtuelle 360° : Guide Complet pour Débutants (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "taille-images-visite-virtuelle-360",
);

useBlogSeo({
  title:
    "Taille et Résolution des Images pour Visites Virtuelles 360° 2026 | Travvir",
  description:
    "Spécifications de résolution pour visites virtuelles 360° : résolutions recommandées par plateforme (Travvir, Google Street View, Kuula), ratio 2:1, et formats de fichiers.",
  keywords:
    "résolution photo 360, taille image visite virtuelle, spécifications photo 360, pixels photo 360, format image visite virtuelle",
  slug: "taille-images-visite-virtuelle-360",
  image: postData?.image,
  postData,
  lang: "fr",
  faqItems: [
    {
      question: "Faut-il compresser les images 360° avant publication ?",
      answer:
        "Non, Travvir gère automatiquement la compression pour la diffusion web tout en conservant le fichier haute résolution.",
    },
    {
      question: "Quelle différence entre JPEG et PNG pour les photos 360° ?",
      answer:
        "Le JPEG est recommandé — excellent rapport qualité/taille. Le PNG produit des fichiers 3× plus lourds sans amélioration visible.",
    },
  ],
});

</script>
